// FILE 18: src/components/catalog/ProductCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import { COLOR_OPTIONS } from './ColorFilter';
import './ProductCard.css';

// Quick lookup: colorKey → swatch hex
const SWATCH_MAP = Object.fromEntries(
  COLOR_OPTIONS.map(({ key, swatch, border }) => [key, { swatch, border }])
);

const CATEGORY_LABELS = {
  marble: 'Marble',
  porcelain: 'Porcelain',
  ceramic: 'Ceramic',
  naturalStone: 'Natural Stone',
  woodLook: 'Wood Look',
  mosaic: 'Mosaic',
};

export default function ProductCard({ product, onViewDetails }) {
  const [imgError, setImgError] = useState(false);

  if (!product) return null;

  const {
    id,
    name,
    category,
    collection,
    finish,
    sizes,
    priceRange,
    images,
    inStock,
    colors,
    patternConfig,
  } = product;

  const primaryImage = images && images[0] ? images[0] : null;
  const fallbackBg = patternConfig?.baseColor || '#c0bbb4';

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (onViewDetails) onViewDetails(product);
  };

  return (
    <div className="product-card" onClick={handleViewDetails} role="article">
      {/* Image */}
      <div className="product-card__image-wrap">
        {primaryImage && !imgError ? (
          <picture>
            <source srcSet={primaryImage.url.replace(/\.(jpg|jpeg|png)$/i, '.webp')} type="image/webp" />
            <img
              className="product-card__image"
              src={primaryImage.url}
              alt={primaryImage.alt || name}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </picture>
        ) : (
          <div
            className="product-card__image-fallback"
            style={{ background: fallbackBg }}
            aria-label={name}
          >
            {name}
          </div>
        )}

        {/* Category badge */}
        <span className="product-card__badge">
          {CATEGORY_LABELS[category] || category}
        </span>

        {/* Size badge */}
        {sizes && sizes.length > 0 && (
          <span className="product-card__size-badge">
            {sizes[0]}"
          </span>
        )}

        {/* Out of stock indicator */}
        {!inStock && (
          <span className="product-card__oos">Out of Stock</span>
        )}

        {/* Quick view overlay */}
        <div className="product-card__quick-view">
          <span>Quick View</span>
        </div>
      </div>

      {/* Body */}
      <div className="product-card__body">
        {collection && (
          <span className="product-card__collection">{collection.charAt(0).toUpperCase() + collection.slice(1)}</span>
        )}
        <h3 className="product-card__name">{name}</h3>
        <div className="product-card__specs">
          <div className="product-card__spec-row">
            <span className="product-card__spec-label">Size</span>
            <span className="product-card__spec-value">
              {sizes && sizes.length > 0 ? sizes.join(' · ') : '—'}
            </span>
          </div>
          <div className="product-card__spec-row">
            <span className="product-card__spec-label">Finish</span>
            <span className="product-card__spec-value">{finish}</span>
          </div>
        </div>
        <div className="product-card__price">Contact for pricing</div>

        {/* Color swatches */}
        {colors && colors.length > 0 && (
          <div className="product-card__colors">
            {colors.map((colorKey) => {
              const info = SWATCH_MAP[colorKey];
              if (!info) return null;
              return (
                <span
                  key={colorKey}
                  className="product-card__color-dot"
                  style={{ background: info.swatch, borderColor: info.border }}
                  title={colorKey.charAt(0).toUpperCase() + colorKey.slice(1)}
                />
              );
            })}
          </div>
        )}

      </div>

      {/* Actions */}
      <div className="product-card__actions">
        <Button variant="primary" size="sm" onClick={handleViewDetails}>
          View Details
        </Button>
        <Link
          to={`/contact?tile=${encodeURIComponent(name)}&type=sample`}
          className="product-card__sample-btn"
          onClick={e => e.stopPropagation()}
        >
          Request Sample
        </Link>
      </div>
    </div>
  );
}
